import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { tableToDataSet, type PerfDataSetInput, type PerfDataSetView } from '@1qa/shared';
import { CircleAlert, Download, FileText, KeyRound, Trash2, Upload } from '@/components/icons';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { timeAgo } from '@/lib/format';
import { formatNumber } from '@/lib/perf';
import { readTableFile } from '@/lib/tableFile';
import { Badge, Button, Card, CardBody, CardHeader, Dialog, Field, Input, Toggle, useConfirm, useToast } from '@/components/ui';

const MASK = '••••••';

/** Saves `csv` as a file in the browser (the script's template). */
export function downloadCsv(fileName: string, csv: string) {
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}

/** A file name from a script's name: "Login journey" -> "login-journey-test-data.csv". */
export const templateFileName = (name: string) => `${name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'script'}-test-data.csv`;

/**
 * Upload (or replace) a data file: read CSV / Excel in the browser, check the header, choose which columns are secret,
 * name it, save. `expected`: the test data names the scripts use, to point out missing or extra columns.
 */
export function DataFileDialog({
  projectId,
  open,
  onClose,
  onSaved,
  replace,
  expected = [],
}: {
  projectId: string;
  open: boolean;
  onClose: () => void;
  onSaved?: (set: PerfDataSetView) => void;
  /** Replace this file's rows (same id, so tests using it get the new rows). */
  replace?: PerfDataSetView;
  expected?: string[];
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const [data, setData] = useState<PerfDataSetInput | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reading, setReading] = useState(false);
  const save = useMutation({
    mutationFn: (body: PerfDataSetInput) => (replace ? api.replacePerfDataSet(replace.id, body) : api.createPerfDataSet(projectId, body)),
    onSuccess: (set) => {
      toast.success(`${set.name}: ${formatNumber(set.rowCount)} rows saved`);
      qc.invalidateQueries({ queryKey: ['perf-data-sets', projectId] });
      onSaved?.(set);
      close();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const close = () => {
    setData(null);
    setError(null);
    onClose();
  };
  const choose = async (file: File | null) => {
    if (!file) return;
    setReading(true);
    setError(null);
    try {
      const table = await readTableFile(file);
      const r = tableToDataSet(replace?.name ?? file.name.replace(/\.(csv|xlsx|txt)$/i, '').slice(0, 120), table);
      if (r.ok) setData({ ...r.data, secretColumns: replace ? r.data.columns.filter((c) => replace.secretColumns.includes(c) || r.data.secretColumns.includes(c)) : r.data.secretColumns });
      else {
        setData(null);
        setError(r.error);
      }
    } catch (e) {
      setData(null);
      setError((e as Error).message);
    } finally {
      setReading(false);
    }
  };
  const missing = data ? expected.filter((k) => !data.columns.includes(k)) : [];
  const extra = data && expected.length ? data.columns.filter((c) => !expected.includes(c)) : [];

  return (
    <Dialog
      open={open}
      onClose={close}
      size="lg"
      title={replace ? `Replace “${replace.name}”` : 'Upload test data'}
      description="A CSV or Excel (.xlsx) file: the first row names the columns (the script’s test data, like USERNAME and PASSWORD), every row under it is one journey."
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button onClick={() => data && save.mutate(data)} disabled={!data || !data.name.trim()} loading={save.isPending} icon={<Upload className="size-4" />}>
            {replace ? 'Replace the rows' : 'Save'}
          </Button>
        </>
      }
    >
      <div className="space-y-4 text-sm">
        <Field label="File" hint="The file is read in your browser; 1QA stores the rows encrypted and never shows secret columns again.">
          {(id) => (
            <label htmlFor={id} className="flex cursor-pointer items-center gap-3 rounded-field border border-dashed border-border px-4 py-3 hover:border-primary">
              <FileText className="size-5 text-fg-muted" aria-hidden />
              <span className="text-fg-secondary">{reading ? 'Reading…' : data ? `${formatNumber(data.rows.length)} rows, ${data.columns.length} columns` : 'Choose a .csv or .xlsx file'}</span>
              <input id={id} type="file" accept=".csv,.txt,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" className="sr-only" onChange={(e) => void choose(e.target.files?.[0] ?? null)} />
            </label>
          )}
        </Field>
        {error && (
          <p role="alert" className="flex items-start gap-2 rounded-field bg-danger-subtle p-3 text-fg ring-1 ring-danger-line ring-inset">
            <CircleAlert className="mt-0.5 size-4 shrink-0 text-danger-text" /> {error}
          </p>
        )}
        {data && (
          <>
            <Field label="Name">{(id) => <Input id={id} value={data.name} maxLength={120} onChange={(e) => setData({ ...data, name: e.target.value })} />}</Field>
            <div>
              <p className="mb-2 text-xs font-medium tracking-label text-fg-muted uppercase">Columns · secret ones are masked in results and never shown again</p>
              <ul className="flex flex-wrap gap-3">
                {data.columns.map((c) => (
                  <li key={c} className="flex items-center gap-2 rounded-field px-3 py-1.5 ring-1 ring-border-subtle ring-inset">
                    <span className="font-mono text-xs text-fg">{c}</span>
                    <Toggle
                      label={`${c} is secret`}
                      checked={data.secretColumns.includes(c)}
                      onChange={(on) => setData({ ...data, secretColumns: on ? [...data.secretColumns, c] : data.secretColumns.filter((x) => x !== c) })}
                    />
                    <span className="text-xs text-fg-muted">secret</span>
                  </li>
                ))}
              </ul>
            </div>
            {(missing.length > 0 || extra.length > 0) && (
              <p className="flex items-start gap-2 rounded-field bg-warning-subtle p-3 text-fg ring-1 ring-warning/40 ring-inset">
                <CircleAlert className="mt-0.5 size-4 shrink-0 text-warning" />
                <span>
                  {missing.length > 0 && `The script also uses ${missing.join(', ')}, which this file does not have: those come from the project’s test data. `}
                  {extra.length > 0 && `${extra.join(', ')} ${extra.length === 1 ? 'is' : 'are'} not used by the script.`}
                </span>
              </p>
            )}
            <div className="overflow-x-auto rounded-card ring-1 ring-border-subtle ring-inset">
              <table className="w-full text-xs">
                <thead className="bg-surface-sunken text-fg-muted">
                  <tr>
                    <th className="px-3 py-1.5 text-left font-medium">#</th>
                    {data.columns.map((c) => (
                      <th key={c} className="px-3 py-1.5 text-left font-mono font-medium">
                        {c}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-subtle">
                  {data.rows.slice(0, 5).map((r, i) => (
                    <tr key={i}>
                      <td className="px-3 py-1.5 text-fg-muted tabular-nums">{i + 1}</td>
                      {r.map((v, k) => (
                        <td key={k} className="max-w-[16rem] truncate px-3 py-1.5 font-mono text-fg">
                          {data.secretColumns.includes(data.columns[k]!) ? (v ? MASK : '') : v}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {data.rows.length > 5 && <p className="px-3 py-1.5 text-xs text-fg-muted">… and {formatNumber(data.rows.length - 5)} more rows</p>}
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
}

/** The project's data files (Scripts tab): rows, columns, which tests use them; upload, replace, delete. */
export function DataFilesCard({ projectId, orgId }: { projectId: string; orgId: string }) {
  const toast = useToast();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const { can, canModify } = useAuth();
  const sets = useQuery({ queryKey: ['perf-data-sets', projectId], queryFn: () => api.perfDataSets(projectId) });
  const [upload, setUpload] = useState(false);
  const [replace, setReplace] = useState<PerfDataSetView | null>(null);
  const remove = useMutation({
    mutationFn: (id: string) => api.deletePerfDataSet(id),
    onSuccess: () => (toast.success('Data file deleted'), qc.invalidateQueries({ queryKey: ['perf-data-sets', projectId] })),
    onError: (e) => toast.error(errorMessage(e)),
  });
  const list = sets.data ?? [];
  return (
    <Card>
      <CardHeader
        title="Test data files"
        description="CSV or Excel rows for performance tests: one row per journey, for example one user’s sign-in per row. Choose a file for a scenario on the performance test’s page. Download a script’s template on its page."
        actions={
          can('test.write', orgId) && (
            <Button variant="outline" size="sm" onClick={() => setUpload(true)} icon={<Upload className="size-4" />}>
              Upload test data
            </Button>
          )
        }
      />
      <CardBody className="text-sm">
        {list.length === 0 ? (
          <p className="text-fg-muted">No data files yet.</p>
        ) : (
          <ul className="divide-y divide-border-subtle">
            {list.map((d) => (
              <li key={d.id} className="flex flex-wrap items-center gap-3 py-2.5">
                <FileText className="size-4 text-fg-muted" aria-hidden />
                <span className="font-medium text-fg">{d.name}</span>
                <span className="text-xs text-fg-secondary">{formatNumber(d.rowCount)} rows</span>
                <span className="flex flex-wrap gap-1">
                  {d.columns.map((c) => (
                    <Badge key={c} tone={d.secretColumns.includes(c) ? 'warning' : 'neutral'}>
                      {d.secretColumns.includes(c) && <KeyRound className="mr-1 size-3" aria-hidden />}
                      {c}
                    </Badge>
                  ))}
                </span>
                <span className="text-xs text-fg-muted">
                  {d.usedBy.length ? `used by ${d.usedBy.map((t) => t.name).join(', ')}` : 'not used yet'} · {timeAgo(d.updatedAt)}
                  {d.createdBy ? ` by ${d.createdBy.name}` : ''}
                </span>
                {d.createdBy && canModify('test.write', d.createdBy.id, orgId) && (
                  <span className="ml-auto flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => setReplace(d)} icon={<Upload className="size-3.5" />}>
                      Replace
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={`Delete ${d.name}`}
                      disabled={d.usedBy.length > 0}
                      title={d.usedBy.length ? 'Used by a performance test' : undefined}
                      onClick={async () => {
                        if (await confirm({ title: 'Delete this data file?', message: `“${d.name}” and its ${formatNumber(d.rowCount)} rows will be deleted.`, confirmLabel: 'Delete' })) remove.mutate(d.id);
                      }}
                      icon={<Trash2 className="size-3.5" />}
                    />
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardBody>
      <DataFileDialog projectId={projectId} open={upload} onClose={() => setUpload(false)} />
      {replace && <DataFileDialog projectId={projectId} open onClose={() => setReplace(null)} replace={replace} />}
    </Card>
  );
}

/** "Download CSV template" for a script: its test data names as columns, an example row from written values. */
export function TemplateButton({ name, columns, csv }: { name: string; columns: string[]; csv: string }) {
  return (
    <Button
      variant="outline"
      size="sm"
      disabled={!columns.length}
      title={columns.length ? `Columns: ${columns.join(', ')}` : 'The script uses no test data yet: make a value test data first'}
      onClick={() => downloadCsv(templateFileName(name), csv)}
      icon={<Download className="size-4" />}
    >
      Download test data template (CSV)
    </Button>
  );
}
